import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './RetailProfitTile.module.scss';
import { useTranslation } from 'react-i18next';
import KPITile from '../../../../../common/KPITile/KPITile';
import KPIDetailTile from '../../../../../common/KPIDetailTile/KPIDetailTile';
import VarianceValue from '../../../../../common/VarianceValue/VarianceValue';
import ViewModeSwitcher, {
  ViewMode,
} from '../../../../../common/ViewModeSwitcher/ViewModeSwitcher';
import GraphCard from '../../../../../common/GraphCard/GraphCard';
import GenericTable from '../../../../../common/GenericTable/GenericTable';
import InsightAI from '../../../../../common/InsightAI/InsightAI';
import Skeleton from '../../../../../common/Skeleton/Skeleton';
import isEmpty from 'lodash/isEmpty';
import { useGetAGCAIDetailsMutation } from '../../../../../../redux/services/agcAIAPI';
import { useGetBusinessPerformanceDetailsQuery } from '../../../../../../redux/services/businessPerformaceAPI';
import { useProfileParams, isAISummariesAccessible } from '../../../../../../utils/common';
import { CurrencyInputFormatter } from '../../../../../../utils/currencyFormatter';
import StackedBarChart from '../../../../../common/StackedBarChart/StackedBarChart';
import { sanitizeStackedBarData } from '../../../../../../utils/stackedBarChartDataSanitizer';

interface SummaryProp {
  summary?: any;
  loading?: boolean;
}

const RetailProfitTile: React.FC<SummaryProp> = ({ summary, loading }) => {
  const { t } = useTranslation();
  const valueNum = typeof summary?.kpiValue === 'number' ? summary?.kpiValue : undefined;

  return (
    <KPITile title={t('retailProfit.title')} dataTestId="retail-profit-tile" loading={loading}>
      <div className={styles.tile_content}>
        <div className={styles.value_wrapper}>
          <div className={styles.value_content}>{CurrencyInputFormatter('USD', valueNum, true)}</div>
          <VarianceValue
            value={summary?.kpiVarianceValue ?? 0}
            dataType={summary?.kpiVarianceDataType}
            comparisonCriteria={summary?.kpiVarianceComparisonCriteria}
            truncateDecimals
          />
        </div>
      </div>
    </KPITile>
  );
};

export default RetailProfitTile;

// ---- Sub-components for Views ---- //

interface RetailProfitMonthlyViewProps {
  viewMode: ViewMode;
  lineChartData: any;
  tableData: any;
  currency: string;
  kpiCode: string;
  chartSummaryData?: ChartSummaryData;
}

const RetailProfitMonthlyView: React.FC<RetailProfitMonthlyViewProps> = ({
  viewMode,
  lineChartData,
  tableData,
  currency,
  chartSummaryData,
  kpiCode,
}) => {
  const { t } = useTranslation();

  if (viewMode === 'left') {
    return isEmpty(lineChartData) ? (
      <div className={styles.no_data}>{t('noDataAvailable')}</div>
    ) : (
      <GraphCard
        lineChartData={lineChartData}
        currency={currency}
        chartSummaryData={chartSummaryData}
        truncateDecimals
      />
    );
  }

  return isEmpty(tableData) ? (
    <div className={styles.no_data}>{t('noDataAvailable')}</div>
  ) : (
    <GenericTable
      tableData={tableData}
      kpiCode={kpiCode}
      highlightRows={[0]}
      truncateDecimals
      isOnlyNegativeHighlight={true}
    />
  );
};

interface RetailProfitYearlyViewProps {
  viewMode: ViewMode;
  tableData: any;
  stackedBarChartData?: any;
  kpiCode: string;
  yAxisValues?: number[];
}

const RetailProfitYearlyView: React.FC<RetailProfitYearlyViewProps> = ({
  viewMode,
  tableData,
  stackedBarChartData,
  kpiCode,
  yAxisValues,
}) => {
  const { t } = useTranslation();

  if (viewMode === 'left') {
    return isEmpty(stackedBarChartData) ? (
      <div className={styles.no_data}>{t('noDataAvailable')}</div>
    ) : (
      <StackedBarChart
        data={stackedBarChartData}
        currency="USD"
        yAxisValues={yAxisValues}
        truncateDecimals
      />
    );
  }

  return isEmpty(tableData) ? (
    <div className={styles.no_data}>{t('noDataAvailable')}</div>
  ) : (
    <GenericTable
      tableData={tableData}
      kpiCode={kpiCode}
      highlightColumns={[1]}
      truncateDecimals
      showSTLY
    />
  );
};

export const RetailProfitDetail: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<ViewMode>('left'); // chart/table switcher
  const [periodCode, setPeriodCode] = useState<string>('monthly');
  const kpiCode = 'retailProfit';
  const { affiliateCode, aboNumber } = useProfileParams();

  const { data: detailsResponse, isLoading } = useGetBusinessPerformanceDetailsQuery(
    { affiliateCode, aboNumber, payload: { kpis: [{ code: kpiCode }] } as any },
    { skip: !onClose }
  );

  const [getAGCAIDetails, { data: aiDetailsResp, isLoading: isLoadingAiSummary }] =
    useGetAGCAIDetailsMutation();
  const hasRequestedRef = useRef(false);
  useEffect(() => {
    const canFetch = Number(affiliateCode) !== 0 && aboNumber !== 0 && Boolean(onClose);
    if (!canFetch || hasRequestedRef.current) return;
    hasRequestedRef.current = true;
    getAGCAIDetails({ affiliateCode, aboNumber, payload: { kpis: [{ code: kpiCode }] } as any });
  }, [affiliateCode, aboNumber, onClose, kpiCode, getAGCAIDetails]);

  const aiRaw = aiDetailsResp?.body;
  const aiResolved = typeof aiRaw === 'string' ? aiRaw : aiRaw?.[kpiCode] || aiRaw?.summary || '';
  const baseAI = aiResolved ? String(aiResolved).slice(0, 300).concat('...') : '';
  const aiContent = baseAI
    ? `${periodCode === 'monthly' ? '[Monthly] ' : '[Yearly] '}${baseAI}`
    : '';

  const detailData = detailsResponse?.body?.kpis?.[0] || {};
  const currency = detailData?.kpiCurrency || 'USD';

  // Extract monthly view data
  const monthlyCharts = detailData?.detail?.monthlyView?.chart || [];
  const monthlyLineChart = monthlyCharts.find((chart: any) => chart?.chartType === 'lineChart');
  const monthlyTableChart = monthlyCharts.find((chart: any) => chart?.chartType === 'table');
  const lineChartData = monthlyLineChart?.chartDetail?.lineChartDetail;
  const monthlyTableData = monthlyTableChart?.chartDetail?.tableDetail;

  // Extract annually view data
  const annuallyCharts = detailData?.detail?.anuallyView?.chart || [];
  const annuallyBarChart = annuallyCharts.find((chart: any) => chart?.chartType === 'barChart');
  const annuallyTableChart = annuallyCharts.find((chart: any) => chart?.chartType === 'table');
  const barChartDetail = annuallyBarChart?.chartDetail?.barChartDetail;
  const annuallyTableData = annuallyTableChart?.chartDetail?.tableDetail;

  const yAxisValues = useMemo(() => {
    if (!barChartDetail?.yAxis || !Array.isArray(barChartDetail.yAxis)) return undefined;
    return barChartDetail.yAxis.map((item: any) => Number(item.dataValue));
  }, [barChartDetail]);

  const stackedBarChartData = useMemo(
    () => sanitizeStackedBarData(barChartDetail, t('retailProfit.title')),
    [barChartDetail, t]
  );

  const tableData = periodCode === 'monthly' ? monthlyTableData : annuallyTableData;

  const periodItems = useMemo(
    () => [
      { code: 'monthly', label: t('monthly_view') },
      { code: 'yearly', label: t('yearly_view') },
    ],
    [t]
  );

  if (isLoading) return <Skeleton height="400px" />;

  const headerContent = (
    <div className={styles.header_content}>
      <div className={styles.tile_header}>
        <div className={styles.title}>{t('retailProfit.title')}</div>
        <div className={styles.desc}>{t('retailProfit.description')}</div>
      </div>
      <ViewModeSwitcher
        mode={viewMode}
        onChangeMode={setViewMode}
        disabled={false}
        timeRanges={periodItems}
        selectedTimeRangeCode={periodCode}
        onSelectTimeRange={setPeriodCode}
      />
    </div>
  );

  const leftContent =
    periodCode === 'monthly' ? (
      <RetailProfitMonthlyView
        viewMode={viewMode}
        lineChartData={lineChartData}
        tableData={tableData}
        currency={currency}
        chartSummaryData={monthlyLineChart?.chartSummary}
        kpiCode={kpiCode}
      />
    ) : (
      <RetailProfitYearlyView
        viewMode={viewMode}
        tableData={tableData}
        stackedBarChartData={stackedBarChartData}
        kpiCode={kpiCode}
        yAxisValues={yAxisValues}
      />
    );

  const rightContent = isAISummariesAccessible() ? (
    isLoadingAiSummary ? (
      <Skeleton height="200px" />
    ) : (
      <InsightAI
        updateDate=""
        content={aiContent}
        title={t('ai_summary')}
        showDropdown={false}
        isDropdownEnabled={true}
        flowerIconSize={20}
        infoIconSize={12}
        titleSize={14}
      />
    )
  ) : null;

  return (
    <KPIDetailTile
      headerContent={headerContent}
      leftContent={leftContent}
      rightContent={rightContent}
      onClose={onClose!}
      dataTestId="retail-profit-detail"
    />
  );
};
